import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

import { PaceSaveActionMessage } from './pace-save-action.interface';

/**
 * Presentational save button paired with an optional status message, in the two layouts the
 * location tracker page needs it in: `inline` puts the message beside the button, as the
 * desktop card footer does, and `stacked` puts a full-width button with the message centred
 * below it, as the mobile bottom bar does.
 *
 * The host owns the surrounding layout (the card footer, the fixed bottom bar) and decides
 * when a message appears and whether it is an error — this component only renders the pair
 * and reports back that the button was pressed.
 */
@Component({
    selector: 'pace-save-action',
    templateUrl: './pace-save-action.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaceSaveActionComponent {
    /** already-translated text for the save button */
    public readonly label = input('');
    /** disables the button while a save is in flight */
    public readonly isSaving = input(false);
    /** status message shown next to (inline) or below (stacked) the button; hidden while undefined */
    public readonly message = input<PaceSaveActionMessage | undefined>(undefined);
    /** `inline` for a card footer, `stacked` for a full-width mobile bar */
    public readonly layout = input<'inline' | 'stacked'>('inline');

    /**
     * Blocks the button for a reason other than a save being in flight — most usefully, a form
     * that is not ready to be submitted yet.
     *
     * Kept apart from {@link isSaving} rather than folded into it: a host that has nothing to save
     * yet is not saving, and a single flag would force it to say so anyway.
     */
    public readonly disabled = input(false);

    public readonly saveRequested = output<void>();
}
